/**
 * /admin/students/:studentId — one student, over time.
 *
 * Four questions: is the student improving, what are they weak at, are they
 * running out of clock, and are they behaving. The topic table is sorted
 * worst-first and the weak ones are labelled, so the answer to "what should
 * they revise next" is the top of the table — with the sample size beside it,
 * because a topic answered twice is not yet evidence of anything.
 *
 * Retakes are the thing this screen most has to get right. A paper can now be
 * sat more than once, so two results for the same exam are a resit rather than
 * two pieces of new ground, and the charts say which is which instead of
 * drawing one rising line and letting it be read as steady progress.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { TriangleAlert, Unplug } from "lucide-react";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Select,
  Table,
  TableEmpty,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { PROCTOR_EVENT_LABEL } from "@exam/shared";
import { useCatalog } from ".";
import { AccuracyBar, WEAK_THRESHOLD, MIN_SAMPLE } from "./components/AccuracyBar";
import { fetchAttemptFacts, scoreSeriesFor } from "./components/AnalyticsData";
import { ChartSeverityBar, ChartSeverityLegend } from "./components/ChartSeverityBar";
import { ChartTimeUse } from "./components/ChartTimeUse";
import { ScoreTrend } from "./components/ScoreTrend";
import {
  EmptyState,
  ErrorNote,
  FixtureBanner,
  Loading,
  PageTitle,
  Stat,
} from "./components/Shell";
import { fetchStudentProgress } from "./data/client";
import { formatDateTime, formatDuration, formatPercent } from "./lib/format";
import { useAsync } from "./lib/useAsync";

export function StudentPage() {
  const { studentId } = useParams();
  const catalog = useCatalog();

  /**
   * `fetchStudentProgress` gives the topic roll-up and the per-attempt flag
   * counts; the attempt facts give the three fields its view models do not
   * carry — which sitting an attempt was, how long it ran against how long it
   * was allowed, and how often the laptop dropped off. Fetched together so the
   * page has a single loading state.
   */
  const state = useAsync(async () => {
    const [progress, allFacts] = await Promise.all([
      fetchStudentProgress(studentId!, catalog),
      fetchAttemptFacts(catalog),
    ]);
    const facts = allFacts.filter((f) => f.student_id === studentId);
    return { progress, facts, series: scoreSeriesFor(facts) };
  }, [studentId, catalog]);

  const [subjectFilter, setSubjectFilter] = useState("");

  const topics = useMemo(() => {
    const rows = state.data?.progress.topics ?? [];
    return subjectFilter
      ? rows.filter((t) => t.subject_name === subjectFilter)
      : rows;
  }, [state.data, subjectFilter]);

  const overall = useMemo(() => {
    const rows = state.data?.progress.topics ?? [];
    const answered = rows.reduce((sum, t) => sum + t.answered, 0);
    const correct = rows.reduce((sum, t) => sum + t.correct, 0);
    return { answered, correct, accuracy: answered === 0 ? 0 : correct / answered };
  }, [state.data]);

  // A topic only counts as weak when there is enough of it to be sure: below
  // the threshold on two answers is a coin toss, not a gap in the syllabus.
  const weakCount = (state.data?.progress.topics ?? []).filter(
    (t) => t.accuracy < WEAK_THRESHOLD && t.answered >= MIN_SAMPLE,
  ).length;

  const totalViolations = (state.data?.progress.violations ?? []).reduce(
    (sum, v) => sum + v.total,
    0,
  );

  const facts = state.data?.facts ?? [];
  const factByAttempt = new Map(facts.map((f) => [f.attempt_id, f]));
  const resits = facts.filter((f) => f.attempt_no > 1).length;
  const totalRejoins = facts.reduce((sum, f) => sum + f.rejoin_count, 0);
  const totalOfflineMs = facts.reduce((sum, f) => sum + f.offline_ms, 0);
  const maxFlagTotal = (state.data?.progress.violations ?? []).reduce(
    (max, v) => Math.max(max, v.severity.critical + v.severity.warn + v.severity.info),
    0,
  );

  return (
    <div className="space-y-4">
      <PageTitle
        title={state.data?.progress.student_name ?? "Student"}
        meta={
          state.data
            ? `${state.data.progress.attempt_count} graded ${
                state.data.progress.attempt_count === 1 ? "sitting" : "sittings"
              }${resits > 0 ? `, ${resits} of them a resit` : ""}`
            : undefined
        }
      />

      <FixtureBanner />

      {state.loading ? (
        <Loading />
      ) : state.error ? (
        <ErrorNote error={state.error} />
      ) : state.data ? (
        <>
          <Card>
            <div className="grid grid-cols-2 divide-x divide-border sm:grid-cols-5">
              <Stat
                label="Overall accuracy"
                value={formatPercent(overall.accuracy)}
                hint={`${overall.correct} of ${overall.answered} answered, this student`}
              />
              <Stat
                label="Topics covered"
                value={state.data.progress.topics.length}
                hint="Topics with at least one answer"
              />
              <Stat
                label="Weak topics"
                value={weakCount}
                tone={weakCount > 0 ? "violation" : "default"}
                hint={`Below ${formatPercent(WEAK_THRESHOLD)} on ${MIN_SAMPLE}+ answers`}
              />
              <Stat
                label="Violations"
                value={totalViolations}
                tone={totalViolations > 0 ? "review" : "default"}
                icon={TriangleAlert}
                hint="Flags across every sitting"
              />
              <Stat
                label="Dropped out"
                value={totalRejoins}
                tone={totalRejoins > 0 ? "review" : "default"}
                icon={Unplug}
                hint={
                  totalRejoins === 0
                    ? "Never lost the connection"
                    : `Rejoins, ${formatDuration(totalOfflineMs)} offline in total`
                }
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Score over time"
              description="Percentage of the paper scored, one panel per subject, oldest sitting first. A dashed step and a square marker mean the same paper was sat again — a resit, not new ground."
            />
            {state.data.series.length === 0 ? (
              <EmptyState>
                Nothing graded yet. The first submitted and marked attempt draws
                the first point.
              </EmptyState>
            ) : (
              <CardBody className="grid gap-6 sm:grid-cols-2">
                {state.data.series.map((series) => (
                  <ScoreTrend key={series.subject_name} series={series} />
                ))}
              </CardBody>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Time management"
              description="How much of the allowed clock each sitting used. The allowance is the exam's duration plus any extra time granted for that attempt, so it can differ between rows."
            />
            <ChartTimeUse facts={state.data.facts} />
          </Card>

          <Card>
            <CardHeader
              title="Topic accuracy"
              description="Weakest first. Unanswered questions are excluded — this measures what the student got wrong, not what they ran out of time for. The n column is how many answers each percentage is drawn from; below it, the percentage is not yet a finding."
              actions={
                <Select
                  label="Subject"
                  value={subjectFilter}
                  onChange={(e) => setSubjectFilter(e.target.value)}
                  className="w-52"
                >
                  <option value="">All subjects</option>
                  {catalog.subjects.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              }
            />
            <Table>
              <THead>
                <tr>
                  <TH>Topic</TH>
                  <TH>Subject</TH>
                  <TH align="right">Week</TH>
                  <TH align="right">Correct</TH>
                  <TH>Accuracy</TH>
                  <TH align="right">Avg time</TH>
                </tr>
              </THead>
              <TBody>
                {topics.length === 0 ? (
                  <TableEmpty colSpan={6}>
                    No graded answers for this subject yet.
                  </TableEmpty>
                ) : (
                  topics.map((topic) => (
                    <TR key={topic.topic_id}>
                      <TD className="font-medium">{topic.topic_name}</TD>
                      <TD className="text-ink-muted">{topic.subject_name}</TD>
                      <TD align="right" className="tabular">
                        {topic.week_no ?? "—"}
                      </TD>
                      <TD align="right" className="tabular">
                        {topic.correct} / {topic.answered}
                      </TD>
                      <TD className="w-72">
                        <AccuracyBar
                          accuracy={topic.accuracy}
                          answered={topic.answered}
                        />
                      </TD>
                      <TD align="right" className="tabular text-ink-muted">
                        {formatDuration(topic.avg_time_ms)}
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </Card>

          <Card>
            <CardHeader
              title="Proctoring history"
              description="Per sitting: flags by severity, and how often the laptop dropped off and came back. A rejoin is not itself a violation — a flaky connection looks the same as a deliberate disconnect from here, which is exactly why the recording is worth opening."
              actions={<ChartSeverityLegend />}
            />
            <Table>
              <THead>
                <tr>
                  <TH>Exam</TH>
                  <TH>Submitted</TH>
                  <TH>Flags</TH>
                  <TH align="right">Critical</TH>
                  <TH align="right">Warnings</TH>
                  <TH align="right">Info</TH>
                  <TH align="right">Rejoins</TH>
                  <TH align="right">Offline</TH>
                  <TH>Most common</TH>
                </tr>
              </THead>
              <TBody>
                {state.data.progress.violations.length === 0 ? (
                  <TableEmpty colSpan={9}>No attempts yet.</TableEmpty>
                ) : (
                  state.data.progress.violations.map((row) => {
                    const fact = factByAttempt.get(row.attempt_id);
                    return (
                      <TR key={row.attempt_id}>
                        <TD>
                          <Link
                            to={`/admin/attempts/${row.attempt_id}`}
                            className="font-medium text-ink hover:underline"
                          >
                            {row.exam_title}
                          </Link>
                          {fact && fact.attempt_no > 1 ? (
                            <span className="ml-1.5 text-2xs text-ink-faint">
                              resit #{fact.attempt_no}
                            </span>
                          ) : null}
                        </TD>
                        <TD className="text-ink-muted">
                          <time>{formatDateTime(row.submitted_at)}</time>
                        </TD>
                        <TD className="w-32">
                          <ChartSeverityBar severity={row.severity} max={maxFlagTotal} />
                        </TD>
                        <TD
                          align="right"
                          className={
                            row.severity.critical > 0
                              ? "tabular font-medium text-violation"
                              : "tabular text-ink-faint"
                          }
                        >
                          {row.severity.critical}
                        </TD>
                        <TD
                          align="right"
                          className={
                            row.severity.warn > 0
                              ? "tabular text-review"
                              : "tabular text-ink-faint"
                          }
                        >
                          {row.severity.warn}
                        </TD>
                        <TD align="right" className="tabular text-ink-faint">
                          {row.severity.info}
                        </TD>
                        <TD
                          align="right"
                          className={
                            fact && fact.rejoin_count > 0
                              ? "tabular text-review"
                              : "tabular text-ink-faint"
                          }
                        >
                          {fact ? fact.rejoin_count : "—"}
                        </TD>
                        <TD align="right" className="tabular text-ink-muted">
                          {fact && fact.offline_ms > 0
                            ? formatDuration(fact.offline_ms)
                            : "—"}
                        </TD>
                        <TD>
                          <div className="flex flex-wrap gap-1">
                            {row.top_types.length === 0 ? (
                              <span className="text-ink-faint">—</span>
                            ) : (
                              row.top_types.map((entry) => (
                                <Badge key={entry.type}>
                                  {PROCTOR_EVENT_LABEL[entry.type]} {entry.count}
                                </Badge>
                              ))
                            )}
                          </div>
                        </TD>
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
          </Card>
        </>
      ) : null}
    </div>
  );
}
